import React, { useCallback } from "react";
import "./WallDoorWindow.css";
import HorizontalSwitch from "src/components/Input/HorizontalSwitch/HorizontalSwitch";
import DoorSelect from "src/components/Input/Select/DoorSelect";
import WindowSelect from "src/components/Input/Select/WindowSelect";
import { Wall } from "src/types/configuration";
// @ts-ignore
import isEqual from "lodash/isEqual";
import HorizontalTabs from "src/components/HorizontalTabs/HorizontalTabs";
import { useConfiguration } from "src/contexts/ConfigurationContext";

interface DoorProps {
  selectedWall?: Wall;
  doorSideComponent: JSX.Element;
  onDoorChange: (window: string) => void;
  onDoorSideChange: (doorSide: string) => void;
}

interface WindowProps {
  selectedWall?: Wall;
  windowSideComponent: JSX.Element;
  onWindowChange: (window: string) => void;
}

interface StorageSideTabsProps {
  wallName?: string;
  wall?: Wall;
  onWallChange: (wall: Wall) => void;
  disabled?: boolean;
  showComponent: boolean;
  setShowComponent: (showComponent: boolean) => void;
}

const WallDoorWindow: React.FC<StorageSideTabsProps> = ({
  wallName,
  wall,
  onWallChange,
  disabled,
  showComponent,
  setShowComponent,
}) => {
  const { state, activeDoorWindowTab, setActiveDoorWindowTab } =
    useConfiguration();

  if (!wall) wall = state.walls.find((wall: Wall) => wall.storage === wallName);
  const handleSideChange = useCallback(
    (newDoorSide: string) => {
      if (!wall || wall.woodWall.doorSide === newDoorSide) return;

      const woodWall = { ...wall.woodWall };
      woodWall.doorSide = newDoorSide === "left" ? "left" : "right";
      const updateWall = { ...wall, woodWall: woodWall };
      if (!isEqual(wall, updateWall)) onWallChange(updateWall);
    },
    [wall, onWallChange]
  );

  const handleWindowSideChange = useCallback(
    (newWindowSide: string) => {
      if (!wall || wall.woodWall.side === newWindowSide) return;

      const woodWall = { ...wall.woodWall };
      woodWall.side = newWindowSide === "left" ? "left" : "right";
      const updateWall = { ...wall, woodWall: woodWall };
      if (!isEqual(wall, updateWall)) onWallChange(updateWall);
    },
    [wall, onWallChange]
  );

  const doorSideComponent = (
    <HorizontalSwitch
      value={wall?.woodWall.doorSide ?? "left"}
      values={[
        { id: 1, label: "Scharnier links", value: "right" },
        { id: 2, label: "Scharnier rechts", value: "left" },
      ]}
      onChange={handleSideChange}
      className={"storage-window-door-side-switch"}
    />
  );

  const windowSideComponent = (
    <HorizontalSwitch
      value={wall?.woodWall.side ?? "left"}
      values={[
        { id: 1, label: "Deur links & \nraam rechts", value: "right" },
        { id: 2, label: "Raam links & \ndeur rechts", value: "left" },
      ]}
      onChange={handleWindowSideChange}
      className={"storage-window-door-side-switch"}
    />
  );

  const DoorComponent: React.FC<DoorProps> = ({
    selectedWall,
    doorSideComponent,
    onDoorChange,
    onDoorSideChange,
  }) => {
    return (
      <>
        {selectedWall?.woodWall.door !== "empty" && doorSideComponent}
        {selectedWall?.woodWall.window !== "empty" &&
          selectedWall?.woodWall.door !== "empty" &&
          windowSideComponent}
        <DoorSelect
          id={`door-select-${wall?.id ?? ""}`}
          wall={wall}
          value={selectedWall?.woodWall.door ?? "empty"}
          subValue={selectedWall?.woodWall.doorSide ?? "left"}
          onChange={onDoorChange}
          onSubChange={onDoorSideChange}
        />
      </>
    );
  };

  const WindowComponent: React.FC<WindowProps> = ({
    selectedWall,
    windowSideComponent,
    onWindowChange,
  }) => {
    const disable =
      selectedWall?.woodWall.door === "dubbele_dichte" ||
      selectedWall?.woodWall.door === "dubble_stapeldorpeldeur";
    return (
      <>
        {selectedWall?.woodWall.window !== "empty" &&
          selectedWall?.woodWall.door !== "empty" &&
          windowSideComponent}
        <WindowSelect
          wall={wall}
          value={selectedWall?.woodWall.window ?? "empty"}
          disableChoice={disable}
          onChange={onWindowChange}
        />
      </>
    );
  };

  const handleDoorChange = (newDoor: string) => {
    if (!wall || wall.woodWall.door === newDoor) return;

    const woodWall = { ...wall.woodWall };
    woodWall.door = newDoor;
    woodWall.window =
      newDoor === "dubbele_dichte" || newDoor === "dubble_stapeldorpeldeur"
        ? "empty"
        : woodWall.window;
    const updateWall = { ...wall, woodWall: woodWall };

    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleWindowChange = (newWindow: string) => {
    if (!wall || wall.woodWall.window === newWindow) return;

    const woodWall = { ...wall.woodWall };
    woodWall.window = newWindow;
    const updateWall = { ...wall, woodWall: woodWall };

    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const handleDoorSideChange = (newDoorSide: string) => {
    if (!wall || wall.woodWall.doorSide === newDoorSide) return;

    const woodWall = { ...wall.woodWall };
    woodWall.doorSide = newDoorSide === "left" ? "left" : "right";
    const updateWall = { ...wall, woodWall: woodWall };
    if (!isEqual(wall, updateWall)) onWallChange(updateWall);
  };

  const tabs = [
    {
      id: 1,
      title: "Deur",
      component: (
        <DoorComponent
          selectedWall={wall}
          onDoorChange={handleDoorChange}
          onDoorSideChange={handleDoorSideChange}
          doorSideComponent={doorSideComponent}
        />
      ),
    },
    {
      id: 2,
      title: "Raam",
      component: (
        <WindowComponent
          selectedWall={wall}
          onWindowChange={handleWindowChange}
          windowSideComponent={windowSideComponent}
        />
      ),
    },
  ];

  return (
    <HorizontalTabs
      tabs={tabs}
      className={"storage-door-window-tabs"}
      value={activeDoorWindowTab}
      onChange={setActiveDoorWindowTab}
    />
  );
};

export default WallDoorWindow;
